import React, { useState, useEffect, useMemo } from "react";
import {
  FaPlus,
  FaSave,
  FaEdit,
  FaTrash,
  FaChevronDown,
  FaChevronUp,
  FaSearch,
} from "react-icons/fa";
import CompanyDropdown from "../../../../DropDowns/CompanyDropdown/CompanyDropdown";
import SearchableList from "../../../../SearchableList/SearchableList";
import AssetDropDown from "../../../../DropDowns/AssetListDropdown/AssetListDropdown";
import Dialog from "../../../FormDialog/Dialog";
import { getUserInfo } from "../../../../../services/authService-sighup";
import { ASSETGROUP_TAB } from "../../../constants/constants";
import { getHeaders } from "../../../../ApiHeaders/apiHeadersEntitlements";
import DomainDropdown from "../../../../DropDowns/DomainDropdown/DomainDropdown";
 
interface AssetGroupFormProps {
  onCreateAssetGroup: (newGroup: any) => void;
  onSaveChanges: (assetGroupId:string,updatedGroup: any) => void;
  onDeleteAssetGroup: (assetGroupId: number) => void;
  onClearSelection: () => void;
  selectedAssetGroup?: any;
  assetGroups: any[];
  onDirtyChange: (dirty: boolean) => void;
  onModeChange: (mode: 'create' | 'edit' | 'view' | null) => void;
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}

interface AssetOption {
  asset_id: number;
  asset_name: string;
  group_status_code?: string;
}

interface FolderPermissionRecord {
  permission_object_id: number;
  folder_name: string;
  access_level: string;
}

interface TopLevelFolderRecord {
  folder_name: string;
}

interface FolderPermissionOption {
  key: string;
  label: string;
  folderName: string;
  accessLevel: "read" | "write";
  selected: boolean;
  permissionObjectId?: number;
}

const apiUrl = process.env.REACT_APP_ENTITLEMENTS_API;
 
const AssetGroupForm: React.FC<AssetGroupFormProps> = ({
  onCreateAssetGroup,
  onSaveChanges,
  onDeleteAssetGroup,
  onClearSelection,
  selectedAssetGroup,
  assetGroups, 
  onDirtyChange,
  onModeChange,
  registerTabSwitchGuard,
  canDelete = false,
}) => {
  const [groupName, setGroupName] = useState("");
  const [selectedDomain, setSelectedDomain] = useState("amp");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [isViewMode, setIsViewMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [assetsData, setAssetsData] = useState<AssetOption[]>([]);
  const [assetGroupId, setassetGroupId] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  // const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [user, setUser] = useState<{ fullName: string; email: string } | null>(null);
  const [modifiedFields, setModifiedFields] = useState<Record<string, boolean>>({});
  const [activeField, setActiveField] = useState<string | null>(null);

  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [selectedAssetForPermissions, setSelectedAssetForPermissions] =
    useState<AssetOption | null>(null);
  const [folderPermissionOptions, setFolderPermissionOptions] = useState<
    FolderPermissionOption[]
  >([]);
  const [loadingFolderPermissions, setLoadingFolderPermissions] =
    useState(false);
  const [folderPermissionSearchTerm, setFolderPermissionSearchTerm] =
    useState("");
  const [expandedPermissionGroups, setExpandedPermissionGroups] = useState<{
    read: boolean;
    write: boolean;
  }>({
    read: false,
    write: false,
  });

  const dynamicStatusGroups = useMemo(() => {
    const compareByName = (a: AssetOption, b: AssetOption) =>
      a.asset_name.localeCompare(b.asset_name);
    const selectedSet = new Set(selectedAssets);
    const fixedStatuses = new Set(["active", "onboarding", "contracted", "retired"]);
    const grouped = new Map<string, { statusLabel: string; assets: AssetOption[] }>();

    assetsData.forEach((asset) => {
      if (selectedSet.has(asset.asset_name)) return;

      const rawStatus = (asset.group_status_code || "").trim();
      const normalizedStatus = rawStatus.toLowerCase();
      if (!normalizedStatus || fixedStatuses.has(normalizedStatus)) return;

      if (!grouped.has(normalizedStatus)) {
        grouped.set(normalizedStatus, {
          statusLabel: rawStatus,
          assets: [],
        });
      }

      grouped.get(normalizedStatus)?.assets.push(asset);
    });

    return Array.from(grouped.values())
      .map((group) => ({
        statusLabel: group.statusLabel,
        assets: group.assets.sort(compareByName),
      }))
      .sort((a, b) => a.statusLabel.localeCompare(b.statusLabel));
  }, [assetsData, selectedAssets]);

  const sortedAssetList = useMemo(() => {
    const compareByName = (a: AssetOption, b: AssetOption) =>
      a.asset_name.localeCompare(b.asset_name);

    const selectedSet = new Set(selectedAssets);

    const selected = assetsData
      .filter((asset) => selectedSet.has(asset.asset_name))
      .sort(compareByName);

    const unselected = assetsData.filter(
      (asset) => !selectedSet.has(asset.asset_name)
    );

    const unselectedActiveOrOnboarding = unselected
      .filter((asset) => {
        const status = (asset.group_status_code || "").trim().toLowerCase();
        return status === "active" || status === "onboarding";
      })
      .sort(compareByName);

    const unselectedContracted = unselected
      .filter((asset) => {
        const status = (asset.group_status_code || "").trim().toLowerCase();
        return status === "contracted";
      })
      .sort(compareByName);

    const unselectedRetired = unselected
      .filter(
        (asset) =>
          (asset.group_status_code || "").trim().toLowerCase() === "retired"
      )
      .sort(compareByName);

    const unselectedDynamic = dynamicStatusGroups.flatMap((group) => group.assets);

    return [
      ...selected,
      ...unselectedActiveOrOnboarding,
      ...unselectedContracted,
      ...unselectedDynamic,
      ...unselectedRetired,
    ];
  }, [assetsData, selectedAssets, dynamicStatusGroups]);

  const assetsList = useMemo(
    () => sortedAssetList.map((asset) => asset.asset_name),
    [sortedAssetList]
  );

  const contractedDividerBeforeItem = useMemo(() => {
    const firstContracted = sortedAssetList.find(
      (asset) =>
        !selectedAssets.includes(asset.asset_name) &&
        (asset.group_status_code || "").trim().toLowerCase() === "contracted"
    );
    return firstContracted?.asset_name || null;
  }, [sortedAssetList, selectedAssets]);

  const retiredDividerBeforeItem = useMemo(() => {
    const firstRetired = sortedAssetList.find(
      (asset) =>
        !selectedAssets.includes(asset.asset_name) &&
        (asset.group_status_code || "").trim().toLowerCase() === "retired"
    );
    return firstRetired?.asset_name || null;
  }, [sortedAssetList, selectedAssets]);

  const dynamicStatusDividers = useMemo(() => {
    return dynamicStatusGroups
      .map((group) => ({
        beforeItem: group.assets[0]?.asset_name || "",
        label: group.statusLabel,
      }))
      .filter((divider) => divider.beforeItem);
  }, [dynamicStatusGroups]);

  const assetByName = useMemo(() => {
    const map = new Map<string, AssetOption>();
    assetsData.forEach((asset) => map.set(asset.asset_name, asset));
    return map;
  }, [assetsData]);

  const folderPermissionLabelMap = useMemo(() => {
    const map = new Map<string, FolderPermissionOption>();
    folderPermissionOptions.forEach((option) => map.set(option.key, option));
    return map;
  }, [folderPermissionOptions]);

  const filteredFolderPermissionOptions = useMemo(() => {
    const query = folderPermissionSearchTerm.trim().toLowerCase();
    if (!query) return folderPermissionOptions;
    return folderPermissionOptions.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [folderPermissionOptions, folderPermissionSearchTerm]);

  const groupedFolderPermissionOptions = useMemo(() => {
    const grouped = {
      read: filteredFolderPermissionOptions
        .filter((option) => option.accessLevel === "read")
        .sort((a, b) => {
          if (a.selected !== b.selected) return a.selected ? -1 : 1;
          return a.label.localeCompare(b.label);
        }),
      write: filteredFolderPermissionOptions
        .filter((option) => option.accessLevel === "write")
        .sort((a, b) => {
          if (a.selected !== b.selected) return a.selected ? -1 : 1;
          return a.label.localeCompare(b.label);
        }),
    };
    return grouped;
  }, [filteredFolderPermissionOptions]);

  useEffect(() => {
          const fetchUserInfo = () => {
            const userInfo = getUserInfo();
            if (userInfo) {
              setUser({
                fullName: userInfo.fullName || "Unknown User",
                email: userInfo.email || "",
              });
            }
          };
          fetchUserInfo();
        }, []);
        
    
  const createdBy = user ? `${user.email || user.fullName}` : "Unknown User";
  const modifiedBy = user ? `${user.email || user.fullName}` : "Unknown User";
  const [dialog, setDialog] = useState<{
        open: boolean;
        title: string;
        message: string;
        confirmButtonText: string;
        cancelButtonText?: string;
        onConfirm: () => void;
        onCancel?: () => void;
      }>({
        open: false,
        title: "",
        message: "",
        confirmButtonText: "",
        onConfirm: () => {},
      });
    
      const closeDialog = () => {
        setDialog({
          open: false,
          title: "",
          message: "",
          confirmButtonText: "",
          onConfirm: () => {},
        });
      };

  const getFolderPermissionKey = (folderName: string, accessLevel: string) =>
    `${folderName.trim().toLowerCase()}__${accessLevel
      .trim()
      .toLowerCase()}`;

  const formatFolderNameForDisplay = (folderName: string) => {
    const normalized = folderName.trim().toLowerCase();
    if (normalized === "_betm_all_folders_") {
      return "BETM_ALL_FOLDERS";
    }
    return folderName.trim().toUpperCase();
  };

  const formatPermissionLabel = (folderName: string, _accessLevel: string) => {
    return formatFolderNameForDisplay(folderName);
  };

  const fetchFolderPermissionOptions = async (asset: AssetOption) => {
    if (!apiUrl) {
      setDialog({
        open: true,
        title: "Missing API Configuration",
        message: "REACT_APP_ENTITLEMENTS_API is not configured.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    setLoadingFolderPermissions(true);
    setSelectedAssetForPermissions(asset);
    setFolderPermissionSearchTerm("");

    try {
      const headers = await getHeaders();
      const response = await fetch(`${apiUrl}/folder-permission/asset/${asset.asset_id}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch folder permissions for asset ${asset.asset_name}`);
      }

      const result = await response.json();
      const data = result?.response?.data || {};

      const permissions: FolderPermissionRecord[] = data.permissions || [];
      const topLevelFolders: TopLevelFolderRecord[] = data.top_level_folders || [];

      const optionsMap = new Map<string, FolderPermissionOption>();

      permissions.forEach((permission) => {
        const accessLevel = permission.access_level?.toLowerCase();
        if (accessLevel !== "read" && accessLevel !== "write") return;

        const key = getFolderPermissionKey(permission.folder_name, accessLevel);
        optionsMap.set(key, {
          key,
          label: formatPermissionLabel(permission.folder_name, accessLevel),
          folderName: permission.folder_name,
          accessLevel,
          selected: true,
          permissionObjectId: permission.permission_object_id,
        });
      });

      topLevelFolders.forEach((folder) => {
        ["read", "write"].forEach((accessLevel) => {
          const key = getFolderPermissionKey(folder.folder_name, accessLevel);
          if (!optionsMap.has(key)) {
            optionsMap.set(key, {
              key,
              label: formatPermissionLabel(folder.folder_name, accessLevel),
              folderName: folder.folder_name,
              accessLevel: accessLevel as "read" | "write",
              selected: false,
            });
          }
        });
      });

      const options = Array.from(optionsMap.values()).sort((a, b) => {
        if (a.selected !== b.selected) return a.selected ? -1 : 1;
        return a.label.localeCompare(b.label);
      });

      setFolderPermissionOptions(options);
    } catch (error) {
      console.error(error);
      setFolderPermissionOptions([]);
      setDialog({
        open: true,
        title: "Folder Permission Error",
        message: "Unable to fetch folder permissions for the selected asset.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } finally {
      setLoadingFolderPermissions(false);
    }
  };

  const createFolderPermission = async (
    asset: AssetOption,
    option: FolderPermissionOption
  ): Promise<boolean> => {
    if (!apiUrl) return false;

    try {
      const headers = await getHeaders();
      const response = await fetch(`${apiUrl}/folder-permission`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...headers,
        },
        body: JSON.stringify({
          asset_id: asset.asset_id,
          folder_name: option.folderName,
          access_level: option.accessLevel,
          service_name: "general_amp_service",
          enabled: "Y",
          sys_created_by: createdBy,
          sys_modified_by: modifiedBy,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create folder permission");
      }
      return true;
    } catch (error) {
      console.error(error);
      setDialog({
        open: true,
        title: "Create Permission Failed",
        message: "Unable to create the selected folder permission.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return false;
    }
  };

  const deleteFolderPermission = async (
    asset: AssetOption,
    option: FolderPermissionOption
  ): Promise<boolean> => {
    if (!apiUrl) return false;

    if (!option.permissionObjectId) {
      setDialog({
        open: true,
        title: "Missing Permission ID",
        message:
          "Cannot delete this mapping because no permission_object_id is available.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return false;
    }

    try {
      const headers = await getHeaders();
      const response = await fetch(
        `${apiUrl}/folder-permission/${option.permissionObjectId}`,
        {
          method: "DELETE",
          headers,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete folder permission");
      }
      return true;
    } catch (error) {
      console.error(error);
      setDialog({
        open: true,
        title: "Delete Permission Failed",
        message: "Unable to remove the selected folder permission.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return false;
    }
  };

  const runBulkFolderPermissionChanges = async (
    options: FolderPermissionOption[],
    nextSelected: boolean
  ) => {
    if (!selectedAssetForPermissions || options.length === 0) return;

    let successCount = 0;
    for (const option of options) {
      const success = nextSelected
        ? await createFolderPermission(selectedAssetForPermissions, option)
        : await deleteFolderPermission(selectedAssetForPermissions, option);
      if (success) successCount += 1;
    }

    if (successCount > 0) {
      await fetchFolderPermissionOptions(selectedAssetForPermissions);
    }
  };

  const handleToggleGroupExpand = (group: "read" | "write") => {
    setExpandedPermissionGroups((prev) => {
      const nextOpen = !prev[group];
      return {
        read: group === "read" ? nextOpen : false,
        write: group === "write" ? nextOpen : false,
      };
    });
  };

  const handleGroupSelectionToggle = (
    group: "read" | "write",
    nextSelected: boolean
  ) => {
    const groupOptions = folderPermissionOptions.filter(
      (option) => option.accessLevel === group
    );

    const pendingOptions = nextSelected
      ? groupOptions.filter((option) => !option.selected)
      : groupOptions.filter((option) => option.selected);

    if (pendingOptions.length === 0 || !selectedAssetForPermissions) return;

    const actionLabel = nextSelected ? "create" : "remove";
    setDialog({
      open: true,
      title: `${nextSelected ? "Create" : "Remove"} ${group.toUpperCase()} Permissions`,
      message: `This will ${actionLabel} ${pendingOptions.length} ${group.toUpperCase()} folder permission mappings for asset ${selectedAssetForPermissions.asset_name}. Continue?`,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        closeDialog();
        void runBulkFolderPermissionChanges(pendingOptions, nextSelected);
      },
      onCancel: closeDialog,
    });
  };

  const handleFolderPermissionToggle = (itemKey: string, nextSelected: boolean) => {
    const option = folderPermissionLabelMap.get(itemKey);
    if (!option || !selectedAssetForPermissions) return;

    if (nextSelected) {
      setDialog({
        open: true,
        title: "Create Folder Permission",
        message: `Do you want to create permission ${option.label} for asset ${selectedAssetForPermissions.asset_name}?`,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          closeDialog();
          void (async () => {
            const success = await createFolderPermission(
              selectedAssetForPermissions,
              option
            );
            if (success) {
              await fetchFolderPermissionOptions(selectedAssetForPermissions);
            }
          })();
        },
        onCancel: closeDialog,
      });
      return;
    }

    setDialog({
      open: true,
      title: "Remove Folder Permission",
      message: `Do you want to remove permission ${option.label} from asset ${selectedAssetForPermissions.asset_name}?`,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        closeDialog();
        void (async () => {
          const success = await deleteFolderPermission(
            selectedAssetForPermissions,
            option
          );
          if (success) {
            await fetchFolderPermissionOptions(selectedAssetForPermissions);
          }
        })();
      },
      onCancel: closeDialog,
    });
  };
  
 
  // const handleCompanyChange = (companyCode: string, companyId: number) => {
  //   setSelectedCompany(companyCode);
  //   // onDirtyChange(true); 
  //   setModifiedFields(prev => ({ ...prev, selectedCompany: true }));
  // };
  const handleCompanyChange = (
    companyCode: string,
    companyId: number
  ) => {
    setSelectedCompany(companyCode);
    setSelectedCompanyId(companyId);
    setModifiedFields(prev => ({ ...prev, selectedCompany: true }));
  };
  
 
  useEffect(() => {
    // if (selectedAssetGroup) {
      if (selectedAssetGroup && !isEditMode) {
      setGroupName(selectedAssetGroup.asset_group_name || "");
      setSelectedDomain(selectedAssetGroup.domain || "amp");
      setassetGroupId(selectedAssetGroup.asset_group_id || "");
      setSelectedCompany(selectedAssetGroup.company_name || "");

  
      // Extract asset names from the assets array
      const assetNames = (selectedAssetGroup.assets || []).map(
        (asset: any) => asset.asset_name
      );
      setSelectedAssets(assetNames);
  
      setIsViewMode(true);
      // setIsEditMode(false);
      onDirtyChange(false);
      setModifiedFields({});
      setSelectedAssetForPermissions(null);
      setFolderPermissionOptions([]);
    // } else {
    } else if (selectedAssetGroup && isEditMode) {
      setGroupName(selectedAssetGroup.asset_group_name || "");
      setSelectedDomain(selectedAssetGroup.domain || "amp");
      setassetGroupId(selectedAssetGroup.asset_group_id || "");
      setSelectedCompany(selectedAssetGroup.company_name || "");

  
      // Extract asset names from the assets array
      const assetNames = (selectedAssetGroup.assets || []).map(
        (asset: any) => asset.asset_name
      );
      setSelectedAssets(assetNames);
      setIsViewMode(true);
      setIsEditMode(false);
      setModifiedFields({});
      setSelectedAssetForPermissions(null);
      setFolderPermissionOptions([]);
    }
  }, [selectedAssetGroup]);
  
  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
  
    const trimmedName = groupName.trim().toLowerCase();
  
    // Check for required fields
    if (!groupName) {
      setDialog({
        open: true,
        title: ASSETGROUP_TAB.VALIDATION.MISSING_INFO_GROUP_TITLE,
        message: ASSETGROUP_TAB.VALIDATION.MISSING_INFO_GROUP_MESSAGE,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }
  
    if (selectedAssets.length === 0) {
      setDialog({
        open: true,
        title: ASSETGROUP_TAB.VALIDATION.MISSING_INFO_TITLE,
        message: ASSETGROUP_TAB.VALIDATION.MISSING_INFO_MESSAGE,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }
  
    const nameAlreadyExists = assetGroups.some((group) => {
      const isSameName = group.asset_group_name.trim().toLowerCase() === trimmedName;
      const isDifferentId = group.asset_group_id !== assetGroupId;
      return isSameName && isDifferentId;
    });
  
    if (nameAlreadyExists) {
      setDialog({
        open: true,
        title: "Duplicate Asset Group Name",
        message: `An asset group with the name "${groupName}" already exists. Please choose a different name.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }
  
    // const newGroup = {
    //   asset_group_name: groupName,
    //   company_name: selectedCompany,
    //   asset_names: selectedAssets,
    //   sys_created_by: createdBy,
    //   sys_modified_by: modifiedBy,
    // };
    const newGroup = {
      asset_group_name: groupName,
      company_name: selectedCompany,
      company_id: selectedCompanyId,
      domain: selectedDomain,
      asset_names: selectedAssets,
      description: "",
      enabled: "Y",
      sys_created_by: createdBy,
      sys_modified_by: modifiedBy,
    };
    
  
    if (isEditMode) {
      if (assetGroupId !== null) {
      setDialog({
        open: true,
        title: ASSETGROUP_TAB.DIALOGS.CONFIRM_EDIT_TITLE,
      message: ASSETGROUP_TAB.DIALOGS.CONFIRM_EDIT_MESSAGE,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          onSaveChanges(assetGroupId,newGroup);
          setModifiedFields({});
          closeDialog();
          resetForm();
        },
        onCancel: closeDialog
      });
    }else {
        console.error("Asset group ID is null during edit mode.");
      }
    } else {
      setDialog({
        open: true,
        title: ASSETGROUP_TAB.DIALOGS.CONFIRM_CREATE_TITLE,
        message: ASSETGROUP_TAB.DIALOGS.CONFIRM_CREATE_MESSAGE,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          onCreateAssetGroup(newGroup);
          closeDialog();
          resetForm();
        },
        onCancel: closeDialog
      });
    }
    setModifiedFields({});
    // onClearSelection();
    setIsViewMode(false);
    setIsEditMode(false);
    // resetForm();
  };
  
  const resetForm = () => {
    setGroupName("");
    setSelectedDomain("amp");
    setSelectedCompany("");
    setSelectedAssets([]);
    setSelectedAssetForPermissions(null);
    setFolderPermissionOptions([]);
    setIsViewMode(false);
    setIsEditMode(false);
    setassetGroupId(null);
    onDirtyChange(false); 
    setModifiedFields({});
    onModeChange(null);
  };
  
 
  const handleNew = (e?: React.MouseEvent) => {
    if (e) e.preventDefault(); 
    setDialog({
      open: true,
      title: ASSETGROUP_TAB.DIALOGS.CONFIRM_NEW_TITLE,
        message: ASSETGROUP_TAB.DIALOGS.CONFIRM_NEW_MESSAGE,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        resetForm();
        onClearSelection();
        closeDialog();
        onModeChange('create');
        onDirtyChange(false);
      },
      onCancel: closeDialog,
    });
  };
  
  const resetToSelectedCompany = () => {
    if (selectedAssetGroup) {
      setGroupName(selectedAssetGroup.asset_group_name || "");
      setSelectedDomain(selectedAssetGroup.domain || "amp");
      setassetGroupId(selectedAssetGroup.asset_group_id || "");
      setSelectedCompany(selectedAssetGroup.company_name || "");

      const assetNames = (selectedAssetGroup.assets || []).map(
        (asset: any) => asset.asset_name
      );
      setSelectedAssets(assetNames);
      setModifiedFields({});
      setIsEditMode(false);
      setIsViewMode(true);
      onDirtyChange(false);
      onModeChange("view");
      setSelectedAssetForPermissions(null);
      setFolderPermissionOptions([]);
    }
  };
  
  const handleCancelClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setDialog({
      open: true,
      title: "Cancel Editing?",
      message: "Are you sure you want to discard changes?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        closeDialog();
        resetToSelectedCompany();
        setModifiedFields({});
      },
      onCancel: closeDialog,
    });
  };
  
  
 
  const handleEdit = () => {
    setIsEditMode(true);
    onDirtyChange(true); 
    setModifiedFields({});
    onModeChange('edit');
  };

  // Without admin the form is view-only: every field stays disabled.
  const fieldsLocked = !canDelete || (!isEditMode && isViewMode);

  const handleDeleteClick = () => {
    if (!assetGroupId) return;
  
    setDialog({
      open: true,
      title: "Confirm Delete",
      message: "Are you sure you want to delete this Asset Group?",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      onConfirm: () => {
        onDeleteAssetGroup(Number(assetGroupId));
        closeDialog();
        resetForm();
      },
      onCancel: closeDialog,
    });
  };

//  console.log(selectedAssets)
  return (
    <div className="bg-white p-6 rounded-lg shadow-md font-poppins">
      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onConfirm={dialog.onConfirm}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}
      <AssetDropDown onAssetsFetched={setAssetsData} />
 
      <form className="space-y-6">
<div className="flex justify-between items-center gap-4 mb-4">

<div>
  {isEditMode && canDelete && (
    <button
      onClick={handleDeleteClick}
      type="button"
      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600"
    >
      <FaTrash className="w-4 h-4" />
      Delete
    </button>
  )}
</div>

<div className="flex gap-4">
  {canDelete && !isViewMode && !isEditMode && (
    <button
      onClick={handleSubmit}
      className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
    >
      <FaPlus className="w-4 h-4" />
      Create
    </button>
  )}

  {canDelete && isViewMode && !isEditMode && (
    <>
      <button
        onClick={(e) => handleNew(e)}
        className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
      >
        <FaPlus className="w-4 h-4" />
        New
      </button>

      <button
        onClick={handleEdit}
        className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
      >
        <FaEdit className="w-4 h-4" />
        Edit
      </button>
    </>
  )}

  {canDelete && isEditMode && (
    <>
      <button
        onClick={handleSubmit}
        className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
      >
        <FaSave className="w-4 h-4" />
        Save
      </button>

      <button
        onClick={handleCancelClick}
        className="flex items-center gap-2 px-4 py-2 bg-[#a6a9ac] text-white font-medium rounded-lg hover:bg-[#474747] transition-colors duration-300"
      >
        Cancel
      </button>
    </>
  )}
</div>

</div>

 
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Asset Group Name<span className="text-red-500 text-lg align-top ml-1">*</span>
            </label>
            <input
              type="text"
              value={groupName}
              // onChange={(e) => setGroupName(e.target.value)}
              onChange={(e) => {
                setGroupName(e.target.value);
                // onDirtyChange(true); 
                setModifiedFields(prev => ({ ...prev, groupName: true }));
              }}
              placeholder="Enter asset group name"
              required
              onFocus={() => setActiveField("groupName")}
              // disabled={isEditMode || isViewMode}
              disabled={fieldsLocked}
              className={`w-full p-3 border rounded-lg bg-gray-50 text-gray-800 shadow-inner text-sm transition-colors duration-300
                ${fieldsLocked ? "bg-[#f9f9f9] text-gray-800 border-gray-300" : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"}
                ${ isEditMode && (activeField === "groupName"|| modifiedFields.groupName)
                ? "border-green-500 focus:ring-2 focus:ring-green-500"
                : isEditMode ? "border-blue-500" : "border-gray-300"}`}
            />
 
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium">Company</label>
            <CompanyDropdown
              selectedCompany={selectedCompany}
              onCompanyChange={handleCompanyChange}
              onFocus={() => setActiveField("selectedCompany")}
              disabled={fieldsLocked}
              className={`w-68 p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 ${
                !isEditMode
                  ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                  : isEditMode && (activeField === "selectedCompany"|| modifiedFields.selectedCompany)
                  ? "border-green-500 focus:ring-2 focus:ring-green-500"
                  : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
              }`}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Domain<span className="text-red-500 text-lg align-top ml-1">*</span>
            </label>
            {/* Fixed once the asset group exists: role mappings to it are keyed by domain. */}
            <DomainDropdown
              selectedDomain={selectedDomain}
              onDomainChange={(val: string) => {
                setSelectedDomain(val);
                setModifiedFields(prev => ({ ...prev, selectedDomain: true }));
              }}
              onFocus={() => setActiveField("selectedDomain")}
              disabled={!canDelete || isEditMode || isViewMode}
              className={`w-68 p-1 border rounded-lg shadow-inner text-sm transition-all duration-200 ${
                !canDelete || isEditMode || isViewMode
                  ? "bg-[#f9f9f9] text-gray-800 border-gray-300"
                  : activeField === "selectedDomain" || modifiedFields.selectedDomain
                  ? "border-green-500 focus:ring-2 focus:ring-green-500"
                  : "bg-white text-black border-blue-500 focus:ring-2 focus:ring-blue-500"
              }`}
            />
          </div>
        </div>
 
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Select Assets<span className="text-red-500 text-lg align-top ml-1">*</span>
            </label>
            <div className="bg-gray-50 p-6 rounded-md border border-gray-300 min-h-[500px]">
              <SearchableList
                items={assetsList}
                selectedItems={selectedAssets}
                onSelectionChange={setSelectedAssets}
                preserveItemOrder={true}
                reorderSelectedOnOutside={true}
                sectionDividers={[
                  ...(contractedDividerBeforeItem
                    ? [{ beforeItem: contractedDividerBeforeItem, label: "Contracted" }]
                    : []),
                  ...dynamicStatusDividers,
                  ...(retiredDividerBeforeItem
                    ? [{ beforeItem: retiredDividerBeforeItem, label: "Retired" }]
                    : []),
                ]}
                disabled={fieldsLocked}
                onRowActionClick={(assetName) => {
                  const asset = assetByName.get(assetName);
                  if (!asset) {
                    setDialog({
                      open: true,
                      title: "Asset Not Found",
                      message: "Unable to resolve selected asset ID.",
                      confirmButtonText: "Close",
                      onConfirm: closeDialog,
                    });
                    return;
                  }
                  void fetchFolderPermissionOptions(asset);
                }}
                rowActionTitle="Load folder permissions"
                activeRowActionItem={selectedAssetForPermissions?.asset_name || null}
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Asset Folder Permissions
            </label>
            <div className="bg-gray-50 p-6 rounded-md border border-gray-300 min-h-[500px]">
              {selectedAssetForPermissions && (
                <p className="text-sm text-gray-600 mb-3">
                  Selected Asset:{" "}
                  <span className="font-medium text-gray-800">
                    {selectedAssetForPermissions.asset_name}
                  </span>
                </p>
              )}

              {!selectedAssetForPermissions && (
                <p className="text-sm text-gray-500">
                  Click the arrow on an asset row to load its folder permissions.
                </p>
              )}

              {selectedAssetForPermissions && loadingFolderPermissions && (
                <p className="text-sm text-gray-500">Loading folder permissions...</p>
              )}

              {selectedAssetForPermissions && !loadingFolderPermissions && (
                <div className="space-y-4">
                  <div className="relative w-full">
                    <FaSearch className="absolute top-3.5 left-3 text-gray-500 w-4 h-4" />
                    <input
                      type="text"
                      value={folderPermissionSearchTerm}
                      onChange={(e) => setFolderPermissionSearchTerm(e.target.value)}
                      className="w-full p-3 pl-9 border border-gray-300 rounded-lg focus:border-blue-500 bg-white text-gray-800 shadow-inner text-sm"
                      placeholder="Search folder permissions"
                    />
                  </div>

                  {(["read", "write"] as const).map((group) => {
                    const groupItems = groupedFolderPermissionOptions[group];
                    const totalCount = folderPermissionOptions.filter(
                      (option) => option.accessLevel === group
                    ).length;
                    const selectedCount = folderPermissionOptions.filter(
                      (option) => option.accessLevel === group && option.selected
                    ).length;
                    const isChecked = totalCount > 0 && selectedCount === totalCount;
                    const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;

                    return (
                      <div
                        key={group}
                        className="bg-white rounded-lg border border-gray-200 shadow-sm"
                      >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              ref={(el) => {
                                if (el) {
                                  el.indeterminate = isIndeterminate;
                                }
                              }}
                              onChange={(e) =>
                                handleGroupSelectionToggle(group, e.target.checked)
                              }
                              disabled={
                                fieldsLocked ||
                                loadingFolderPermissions ||
                                totalCount === 0
                              }
                              className="w-4 h-4 accent-blue-600"
                            />
                            <span className="font-medium text-gray-800 capitalize">
                              {group}
                            </span>
                            <span
                              className={`text-xs ${
                                selectedCount > 0
                                  ? "text-blue-600 font-medium"
                                  : "text-gray-500"
                              }`}
                            >
                              ({selectedCount}/{totalCount})
                            </span>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleToggleGroupExpand(group)}
                            aria-expanded={expandedPermissionGroups[group]}
                            aria-label={`${expandedPermissionGroups[group] ? "Collapse" : "Expand"} ${group} permissions`}
                            className="p-1 rounded text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          >
                            {expandedPermissionGroups[group] ? (
                              <FaChevronUp className="w-4 h-4" />
                            ) : (
                              <FaChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </div>

                        {expandedPermissionGroups[group] && (
                          <div className="max-h-56 overflow-y-auto px-2 py-2">
                            {groupItems.length === 0 ? (
                              <p className="text-sm text-gray-500 px-2 py-3">
                                No {group} permissions found.
                              </p>
                            ) : (
                              groupItems.map((option) => (
                                <label
                                  key={option.key}
                                  className="flex items-center gap-2 px-2 py-2 rounded hover:bg-blue-50 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={option.selected}
                                    onChange={(e) =>
                                      handleFolderPermissionToggle(
                                        option.key,
                                        e.target.checked
                                      )
                                    }
                                    disabled={fieldsLocked}
                                    className="w-4 h-4 accent-blue-600"
                                  />
                                  <span className="text-sm text-gray-800">
                                    {option.label}
                                  </span>
                                </label>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
 
export default AssetGroupForm;